import { EditOutlined, FilterFilled } from "@ant-design/icons";
import { Checkbox, DatePicker, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import type { CSSProperties } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MachineItem, TaskExecution, TaskHubItem } from "shared/api";
import { DatePickerValue, TableSearchFilter } from "shared/ui";

function getDaysOverdueCellStyle(
  days: number | null | undefined
): CSSProperties {
  if (days == null) return {};
  if (days === 0) return { backgroundColor: "#bbf7d0" };
  if (days >= 1 && days <= 7) return { backgroundColor: "#fef08a" };
  return { backgroundColor: "#fecaca" };
}

interface useGetTaskExecutionsColumnsProps {
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  handleTaskNameValue: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  taskNameValue: string;
  handleExpectedDateGreater: (
    date: DatePickerValue,
    dateString: string
  ) => void;
  expectedDateGreater: string | null;
  handleExpectedDateLesser: (date: DatePickerValue, dateString: string) => void;
  expectedDateLesser: string | null;
  isExpectedFilterActive: boolean;
  handleExecutedDateGreater: (
    date: DatePickerValue,
    dateString: string
  ) => void;
  handleExecutedDateLesser: (date: DatePickerValue, dateString: string) => void;
  executedDateGreater: string | null;
  executedDateLesser: string | null;
  isExecutedFilterActive: boolean;
  handleTaskMessageValue: (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  taskMessageValue: string;
  handlePriorityGte: (value: number | null) => void;
  handlePriorityLte: (value: number | null) => void;
  priorityGte: number | null;
  priorityLte: number | null;
  isPriorityFilterActive: boolean;
  openStatusModal: (rec: TaskExecution) => void;
  openUserModal: (rec: TaskExecution) => void;
  selectedUserName: string | null;
  setSelectedUserName: React.Dispatch<React.SetStateAction<string | null>>;
  machinesData: MachineItem[] | undefined;
  selectedMachines: number[];
  setSelectedMachines: React.Dispatch<React.SetStateAction<number[]>>;
  statusValues: string[];
  setStatusValues: React.Dispatch<React.SetStateAction<string[]>>;
}

export const useGetTaskExecutionsColumns = ({
  transformedUsers,
  handleTaskNameValue,
  taskNameValue,
  handleExpectedDateGreater,
  expectedDateGreater,
  handleExpectedDateLesser,
  isExpectedFilterActive,
  expectedDateLesser,
  executedDateGreater,
  executedDateLesser,
  handleExecutedDateGreater,
  handleExecutedDateLesser,
  isExecutedFilterActive,
  handleTaskMessageValue,
  taskMessageValue,
  handlePriorityGte,
  handlePriorityLte,
  priorityGte,
  priorityLte,
  isPriorityFilterActive,
  openStatusModal,
  openUserModal,
  selectedUserName,
  setSelectedUserName,
  machinesData,
  selectedMachines,
  setSelectedMachines,
  setStatusValues,
  statusValues,
}: useGetTaskExecutionsColumnsProps): ColumnsType<TaskExecution> => {
  const { t } = useTranslation();

  return [
    {
      title: t("taskHub.logTable.expectedTime") || "",
      dataIndex: "timestamp_expected",
      sorter: (a, b) =>
        dayjs(a.timestamp_expected).valueOf() -
        dayjs(b.timestamp_expected).valueOf(),
      render: (value: string) => {
        const date = dayjs(value);
        return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : "N/A";
      },
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">{t("Start From")}</div>
          <DatePicker
            showTime
            value={expectedDateGreater ? dayjs(expectedDateGreater) : null}
            onChange={(val) =>
              handleExpectedDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">{t("Start To")}</div>
          <DatePicker
            showTime
            value={expectedDateLesser ? dayjs(expectedDateLesser) : null}
            onChange={(val) =>
              handleExpectedDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isExpectedFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.logTable.executedTime") || "",
      dataIndex: "timestamp_executed",
      sorter: (a, b) =>
        dayjs(a.timestamp_executed).valueOf() -
        dayjs(b.timestamp_executed).valueOf(),
      render: (value: string) => {
        const date = dayjs(value);
        return date.isValid() ? date.format("YYYY-MM-DD HH:mm:ss") : "N/A";
      },
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">{t("Start From")}</div>
          <DatePicker
            showTime
            value={executedDateGreater ? dayjs(executedDateGreater) : null}
            onChange={(val) =>
              handleExecutedDateGreater(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">{t("Start To")}</div>
          <DatePicker
            showTime
            value={executedDateLesser ? dayjs(executedDateLesser) : null}
            onChange={(val) =>
              handleExecutedDateLesser(val, val?.toISOString() || "")
            }
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isExecutedFilterActive ? "!text-yellow-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.logTable.taskLog") || "",
      dataIndex: "message",
      render: (value: string) =>
        value?.trim() || t("taskHub.logTable.noMessage"),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={() => handleTaskMessageValue("")}
          confirm={confirm}
          value={taskMessageValue}
          handleFilterChange={handleTaskMessageValue}
          visible={visible}
          title={t("Message") || ""}
          placeholder={t("Search by message") || ""}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={
            taskMessageValue.length > 0 ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.logTable.machine") || "",
      key: "machine",
      render: (_, r: TaskExecution) =>
        r.machine?.name ?? t("taskHub.machines.na"),

      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px] flex flex-col space-y-1 max-h-[200px] overflow-y-auto">
          {machinesData?.map((machine) => (
            <Checkbox
              key={machine.id}
              checked={selectedMachines.includes(machine.id)}
              onChange={() => {
                const newSelection = selectedMachines.includes(machine.id)
                  ? selectedMachines.filter((id) => id !== machine.id)
                  : [...selectedMachines, machine.id];
                setSelectedMachines(newSelection);
                confirm();
              }}
            >
              {machine.name}
            </Checkbox>
          ))}
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            selectedMachines.length > 0
              ? "!text-orange-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.logTable.priority") || "",
      dataIndex: "priority",
      key: "priority",
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">{t("Priority From")}</div>
          <Select
            allowClear
            placeholder={t("Select min priority")}
            value={priorityGte !== null ? priorityGte : undefined}
            onChange={(value) => handlePriorityGte(value ?? null)}
            style={{ width: "100%", marginBottom: 8 }}
            options={[
              { label: "0", value: 0 },
              { label: "1", value: 1 },
              { label: "2", value: 2 },
              { label: "3", value: 3 },
              { label: "4", value: 4 },
              { label: "5", value: 5 },
            ]}
          />
          <div className="mb-1 text-sm">{t("Priority To")}</div>
          <Select
            allowClear
            placeholder={t("Select max priority")}
            value={priorityLte !== null ? priorityLte : undefined}
            onChange={(value) => handlePriorityLte(value ?? null)}
            style={{ width: "100%" }}
            options={[
              { label: "0", value: 0 },
              { label: "1", value: 1 },
              { label: "2", value: 2 },
              { label: "3", value: 3 },
              { label: "4", value: 4 },
              { label: "5", value: 5 },
            ]}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            isPriorityFilterActive ? "!text-purple-600 scale-125" : undefined
          }
        />
      ),
      render: (value: number) =>
        typeof value === "number" ? value : "No priority",
    },
    {
      title: t("taskHub.logTable.status") || "",
      dataIndex: "status",
      key: "status",
      filterDropdown: () => (
        <div className="p-2 w-[200px]">
          <div className="mb-1 text-sm">{t("Status")}</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder={t("Select status")}
            value={statusValues}
            onChange={setStatusValues}
            style={{ width: "100%" }}
            options={[
              { label: "All", value: "" },
              { label: "COMPLETED", value: "COMPLETED" },
              { label: "FAILED", value: "FAILED" },
              { label: "PENDING", value: "PENDING" },
            ]}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            statusValues.length > 0 ? "!text-green-600 scale-125" : undefined
          }
        />
      ),
      render: (value: string, record) => (
        <span className="flex items-center gap-2">
          {t(`taskHub.status.${value.toLowerCase()}`) || value}
          <EditOutlined
            className="cursor-pointer text-blue-600"
            onClick={() => openStatusModal(record)}
          />
        </span>
      ),
    },
    {
      title: t("taskHub.logTable.task") || "",
      dataIndex: "task",
      key: "task",
      render: (task: TaskHubItem) => <span>{task?.name || "—"}</span>,
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={() => handleTaskNameValue("")}
          confirm={confirm}
          value={taskNameValue}
          handleFilterChange={handleTaskNameValue}
          visible={visible}
          title={t("Task Name") || ""}
          placeholder={t("Search by task name") || ""}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={
            taskNameValue.length > 0 ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.logTable.user") || "",
      dataIndex: "user",

      render: (userId: number, record) => {
        const label =
          transformedUsers.find((u) => u.value === userId)?.label ||
          t("taskHub.logTable.unknownUser");
        return (
          <span className="flex items-center gap-2">
            {label}
            <EditOutlined
              className="cursor-pointer text-green-600"
              onClick={() => openUserModal(record)}
            />
          </span>
        );
      },

      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px] flex flex-col space-y-1 max-h-[200px] overflow-y-auto">
          {transformedUsers.map((user) => (
            <Checkbox
              key={user.value}
              checked={selectedUserName === user.label}
              onChange={() => {
                const newName =
                  selectedUserName === user.label ? null : user.label;
                setSelectedUserName(newName);
                confirm();
              }}
            >
              {user.label}
            </Checkbox>
          ))}
        </div>
      ),

      filterIcon: (
        <FilterFilled
          className={selectedUserName ? "!text-blue-600 scale-125" : undefined}
        />
      ),
    },
    {
      title: t("taskHub.logTable.daysOverdue") || "Days overdue",
      dataIndex: "calc_days_overdue",
      key: "calc_days_overdue",
      align: "center",
      sorter: (a, b) => {
        const av = a.calc_days_overdue;
        const bv = b.calc_days_overdue;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av - bv;
      },
      render: (value: number | null | undefined) =>
        value == null ? "" : String(value),
      onCell: (record) => ({
        style: getDaysOverdueCellStyle(record.calc_days_overdue),
      }),
    },
  ];
};
